/**
 * The two pax shapes a composed trip carries, and who reads which (KD-3608).
 *
 * - `composition` — the trip's aggregate pax (adults / children /
 *   childrenBirthdates / count). Experiences, extras and previous bookings
 *   price on the whole group, so they read this one.
 * - `composition.roomsComposition` — the per-room split set on the wizard's
 *   "split across rooms" step. ONE ENTRY = ONE PHYSICAL ROOM. Hotel
 *   availability, room selection and hotel pricing read this one.
 *
 * The DMC app used to join them through a third field, `tripFilters.tripComposition`,
 * which every writer collapsed to a single room holding the whole group — so a
 * trip set up as 2 rooms x 2 adults asked hotels for one room for 4 and got
 * offered a family suite. It was never persisted and never rehydrated either,
 * so a reopened trip lost the split entirely. These two fields are the only pax
 * sources now, in both apps.
 *
 * The shapes themselves live in ./types/roomsComposition and are re-exported
 * below, so `@koob/utils` exposes them from the same place as the functions.
 */
import { toDateString } from '@koob/dates';
import { TravelerKindEnum } from '@koob/enums';
import type {
  ChildBirthdate,
  Composition,
  CompositionWithRooms,
  Pax,
  Room,
  RoomFilters,
} from './types';

export type {
  ChildBirthdate,
  Composition,
  CompositionWithRooms,
  NationalityValue,
  Pax,
  Room,
  RoomFilters,
} from './types';

/** The aggregate pax shape, from a trip's raw adult/children fields. */
export function compositionFromPax({
  adults,
  childrenBirthdates,
}: { adults?: number; childrenBirthdates?: ChildBirthdate[] } = {}): Pax {
  const birthdates = childrenBirthdates ?? [];

  return {
    adults: adults ?? 0,
    children: birthdates.length,
    childrenBirthdates: birthdates,
    count: (adults ?? 0) + birthdates.length,
  };
}

/**
 * Guarantee `composition.roomsComposition`, deriving a single room holding the
 * whole group when the trip predates the split. Applied on hydration so no
 * consumer ever sees a composition without one.
 */
export function withRoomsComposition(
  composition: Composition,
): CompositionWithRooms;
export function withRoomsComposition(
  composition?: Composition | null,
): CompositionWithRooms | null | undefined;
export function withRoomsComposition(
  composition?: Composition | null,
): CompositionWithRooms | null | undefined {
  if (!composition) return composition as null | undefined;
  if (composition.roomsComposition?.length)
    return composition as CompositionWithRooms;

  // eslint-disable-next-line no-unused-vars
  const { roomsComposition, ...pax } = composition;
  return { ...composition, roomsComposition: [pax] };
}

/** Per-room split of a composition, or a single room holding the whole group. */
export function roomsCompositionFrom(composition?: Composition | null): Room[] {
  return withRoomsComposition(composition)?.roomsComposition ?? [];
}

/**
 * Shape each room the way the availability endpoint expects. `firstAdultNationality`
 * is an option object on the wizard side but a plain alpha-2 string over the wire.
 */
/**
 * KD-3758 — the trip's children birthdates dealt out to its rooms in order,
 * each room taking as many as its `children` count. The wizard kept every
 * birthdate on the first room whatever the split, so a trip of 2 rooms of
 * 1 adult + 1 child asked hotels for a room of 3 and a room of 1 — and was
 * offered extra beds. Rooms whose counts do not add up to the trip's children
 * are left as they are.
 */
function withChildrenBirthdatesSplit(
  rooms: Room[],
  birthdates?: ChildBirthdate[],
): Room[] {
  const childrenTotal = rooms.reduce(
    (sum, room) => sum + (room?.children ?? 0),
    0,
  );
  if (!birthdates?.length || childrenTotal !== birthdates.length) {
    return rooms;
  }

  let nextChild = 0;
  return rooms.map((room) => {
    const children = room?.children ?? 0;
    const childrenBirthdates = birthdates.slice(
      nextChild,
      nextChild + children,
    );
    nextChild += children;
    return { ...room, childrenBirthdates };
  });
}

export function roomsCompositionToFilters(
  composition?: Composition | null,
): RoomFilters[] {
  return withChildrenBirthdatesSplit(
    roomsCompositionFrom(composition),
    composition?.childrenBirthdates,
  ).map((room) => ({
    adults: room?.adults ?? 0,
    children: room?.children ?? 0,
    childrenBirthdates: room?.childrenBirthdates ?? [],
    firstAdultNationality:
      typeof room?.firstAdultNationality === 'string'
        ? room.firstAdultNationality
        : room?.firstAdultNationality?.value,
    count: 1,
  }));
}

export function getStayRoomsFilters(
  stayRooms: Room[] | null | undefined,
  composition?: Composition | null,
): RoomFilters[] {
  if (!stayRooms?.length) return roomsCompositionToFilters(composition);

  const tripRooms = roomsCompositionFrom(composition);
  return roomsCompositionToFilters({
    roomsComposition: stayRooms.map((room, index) => ({
      ...room,
      firstAdultNationality:
        room.firstAdultNationality ??
        (tripRooms[index] ?? tripRooms[0])?.firstAdultNationality,
    })),
  });
}

export function clearStayRoomsExceedingTrip<
  T extends { roomsComposition?: Room[] | null },
>(days: T[], composition?: Composition | null): T[] {
  const tripAdults = composition?.adults ?? 0;
  const tripBirthdates = (composition?.childrenBirthdates ?? []).map((date) =>
    toDateString(date),
  );

  const fitsTrip = (rooms: Room[]) => {
    const adults = rooms.reduce((sum, room) => sum + (room?.adults ?? 0), 0);
    const pool = [...tripBirthdates];
    return (
      adults <= tripAdults &&
      rooms
        .flatMap((room) => room?.childrenBirthdates ?? [])
        .every((date) => {
          const index = pool.indexOf(toDateString(date));
          if (index === -1) return false;
          pool.splice(index, 1);
          return true;
        })
    );
  };

  let changed = false;
  const nextDays = days.map((day) => {
    if (!day?.roomsComposition?.length || fitsTrip(day.roomsComposition)) {
      return day;
    }
    changed = true;
    return {
      ...day,
      roomsComposition: null,
      selectedRoom: null,
      hotelPricing: null,
      hotelSelectedRoomKey: null,
    };
  });

  return changed ? nextDays : days;
}

export function getBookedRoomsComposition(
  roomsBooked?:
    | { travelers?: { kind?: string; birthdate?: string | null }[] | null }[]
    | null,
): Room[] {
  return (roomsBooked ?? []).map((room) => {
    const travelers = room?.travelers ?? [];
    const children = travelers.filter(
      (traveler) => traveler?.kind !== TravelerKindEnum.ADULT,
    );
    return {
      adults: travelers.length - children.length,
      children: children.length,
      childrenBirthdates: children.map((child) => child?.birthdate ?? null),
    };
  });
}

export function isSameRoomsSplit(
  rooms?: Room[] | null,
  otherRooms?: Room[] | null,
): boolean {
  const split = (list?: Room[] | null) =>
    (list ?? [])
      .map(
        (room) =>
          `${room?.adults ?? 0}~${
            room?.childrenBirthdates?.length ?? room?.children ?? 0
          }`,
      )
      .join('|');
  return split(rooms) === split(otherRooms);
}
